import { useCallback } from 'react';
import { useDialogForm } from './useDialogForm';
import type { DialogFormState, FormFieldValue } from './useDialogForm';

type DialogFormCloseOptions = { reset?: boolean; clearError?: () => void };

export type DialogFormStateWithHandlers<T extends Record<string, FormFieldValue>> =
  DialogFormState<T> & {
    handleClose: (close: () => void, options?: DialogFormCloseOptions) => void;
    resetDialog: () => void;
  };

export const useDialogFormState = <T extends Record<string, FormFieldValue>>(
  initial: T,
): DialogFormStateWithHandlers<T> => {
  const form = useDialogForm(initial);
  const { reset, setSubmitError } = form;

  const resetDialog = useCallback(() => {
    reset();
  }, [reset]);

  const handleClose = useCallback(
    (close: () => void, options?: DialogFormCloseOptions) => {
      close();
      if (options?.reset ?? true) {
        reset();
      } else {
        setSubmitError(null);
      }
      options?.clearError?.();
    },
    [reset, setSubmitError],
  );

  return { ...form, handleClose, resetDialog };
};
